/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays fallback UI
 * 
 * Features:
 * - Prevents white screen crashes
 * - Logs errors for debugging
 * - Provides user-friendly error messages
 * - Allows retry functionality
 * - Integrates with authentication system
 * 
 * @author Employee Management System
 * @version 3.0.0
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, RefreshCw, Home, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('🚨 ErrorBoundary caught an error:', {
      error,
      errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    this.setState({
      error,
      errorInfo
    });

    // Log to external service if available
    if (this.props.onError && typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleLogout = () => {
    // Clear all data and redirect to login
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, retryCount } = this.state;
      const { 
        fallback, 
        showDetails = false,
        maxRetries = 3,
        title = i18n.t('errorBoundary.defaultTitle'),
        message = i18n.t('errorBoundary.defaultMessage'),
        showRetry = true,
        showGoHome = true,
        showLogout = true
      } = this.props;

      // If custom fallback is provided, use it
      if (fallback) {
        return typeof fallback === 'function' 
          ? fallback(error, errorInfo, this.handleRetry)
          : fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-xl font-bold text-gray-900 text-center mb-4">
              {title}
            </h1>

            {/* Error Message */}
            <p className="text-gray-600 text-center mb-6">
              {message}
            </p>

            {/* Retry Information */}
            {retryCount > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-yellow-800 text-center">
                  Deneme sayısı: {retryCount}/{maxRetries}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Retry Button */}
              {showRetry && retryCount < maxRetries && (
                <button
                  onClick={this.handleRetry}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tekrar Dene
                </button>
              )}

              {/* Go Home Button */}
              {showGoHome && (
                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                >
                  <Home className="w-4 h-4" />
                  Ana Sayfaya Dön
                </button>
              )}

              {/* Logout Button */}
              {showLogout && (
                <button
                  onClick={this.handleLogout}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                >
                  <LogOut className="w-4 h-4" />
                  Çıkış Yap ve Temizle
                </button>
              )}
            </div>

            {/* Error Details (Development/Debug Mode) */}
            {showDetails && error && (
              <details className="mt-6 bg-gray-50 rounded-lg p-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  Hata Detayları (Geliştirici)
                </summary>
                <div className="mt-3 text-xs text-gray-600 font-mono">
                  <div className="mb-2">
                    <strong>Hata:</strong>
                    <pre className="mt-1 whitespace-pre-wrap break-all">
                      {error.toString()}
                    </pre>
                  </div>
                  {errorInfo && (
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="mt-1 whitespace-pre-wrap break-all">
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Help Text */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Sorun devam ederse, sistem yöneticisiyle iletişime geçin.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onError: PropTypes.func,
  showDetails: PropTypes.bool,
  maxRetries: PropTypes.number,
  title: PropTypes.string,
  message: PropTypes.string,
  showRetry: PropTypes.bool,
  showGoHome: PropTypes.bool,
  showLogout: PropTypes.bool
};

/**
 * Higher-order component to wrap any component with error boundary
 */
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  const WithErrorBoundaryComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
};

/**
 * Hook to manually trigger error boundary (for functional components)
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return React.useCallback((error) => {
    setError(error);
  }, []);
};

/**
 * Specialized Error Boundary for Route Components
 */
export const RouteErrorBoundary = ({ children }) => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      title={t('errorBoundary.pageLoadTitle')}
      message={t('errorBoundary.pageLoadMessage')}
      showDetails={process.env.NODE_ENV === 'development'}
      maxRetries={2}
    >
      {children}
    </ErrorBoundary>
  );
};

RouteErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Specialized Error Boundary for API Components
 */
export const ApiErrorBoundary = ({ children, onRetry }) => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      title={t('errorBoundary.dataLoadTitle')}
      message={t('errorBoundary.dataLoadMessage')}
      showDetails={false}
      maxRetries={3}
      onError={(error, errorInfo) => {
        console.error('API Error:', { error, errorInfo });
        if (onRetry) onRetry();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

ApiErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onRetry: PropTypes.func
};

export default ErrorBoundary;
